import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Check } from 'lucide-react';
import { ManufacturedProduct } from '../../types';

interface ProductAllocationSelectProps {
  selectedProductIds: string[];
  availableProducts: ManufacturedProduct[];
  onChange: (productIds: string[]) => void;
  disabled?: boolean;
}

const ProductAllocationSelect: React.FC<ProductAllocationSelectProps> = ({
  selectedProductIds,
  availableProducts,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredProducts = availableProducts.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedProducts = availableProducts.filter(product =>
    selectedProductIds.includes(product.id)
  );

  const toggleProduct = (productId: string) => {
    if (selectedProductIds.includes(productId)) {
      onChange(selectedProductIds.filter(id => id !== productId));
    } else {
      onChange([...selectedProductIds, productId]);
    }
  };

  const removeProduct = (productId: string) => {
    onChange(selectedProductIds.filter(id => id !== productId));
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Allocated to Manufactured Products
      </label>

      <div className="min-h-[42px] w-full px-3 py-2 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent bg-white">
        <div className="flex flex-wrap gap-2">
          {selectedProducts.map(product => (
            <span
              key={product.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
            >
              <span>{product.name}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeProduct(product.id)}
                  className="hover:bg-blue-200 rounded p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
          {!disabled && (
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="text-sm text-gray-500 hover:text-gray-700 px-2 py-1"
            >
              {selectedProducts.length === 0 ? 'Select products...' : 'Add more...'}
            </button>
          )}
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-600" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {filteredProducts.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                No products found
              </div>
            ) : (
              filteredProducts.map(product => {
                const isSelected = selectedProductIds.includes(product.id);
                const isDiscontinued = product.status === 'discontinued';

                return (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => !isDiscontinued && toggleProduct(product.id)}
                    disabled={isDiscontinued}
                    className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center justify-between transition-colors ${
                      isDiscontinued ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {product.name}
                        </span>
                        {isDiscontinued && (
                          <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded">
                            Discontinued
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-500">SKU: {product.sku}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${
                          product.status === 'production' ? 'bg-green-100 text-green-800' :
                          product.status === 'prototype' ? 'bg-yellow-100 text-yellow-800' :
                          product.status === 'design' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {product.status}
                        </span>
                      </div>
                    </div>
                    {isSelected && (
                      <Check className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="p-2 border-t border-gray-200 bg-gray-50">
            <div className="text-xs text-gray-600">
              {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
            </div>
          </div>
        </div>
      )}

      <div className="mt-1">
        {selectedProducts.length === 0 && (
          <p className="text-sm text-gray-500">
            Select which manufactured products this supply is allocated to
          </p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Note: Allocations sync with Bill of Materials. Quantities are automatically managed.
        </p>
      </div>
    </div>
  );
};

export default ProductAllocationSelect;
